import { forwardRef, useCallback, useEffect, useRef } from 'react'
import type { ChangeEvent, TextareaHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  autoResize?: boolean
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { className, rows = 4, autoResize = false, style, onChange, value, ...props },
  ref,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null)

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [ref],
  )

  const resize = useCallback(() => {
    if (!autoResize || !innerRef.current) return
    const element = innerRef.current
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
  }, [autoResize])

  useEffect(() => {
    resize()
  }, [resize, value])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      if (autoResize) {
        event.target.style.height = 'auto'
        event.target.style.height = `${event.target.scrollHeight}px`
      }
      onChange?.(event)
    },
    [autoResize, onChange],
  )

  return (
    <textarea
      ref={setRefs}
      rows={rows}
      className={cn(
        'block w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-slate-400 shadow-inner focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/60',
        className,
      )}
      onChange={handleChange}
      style={autoResize ? { ...style, overflowY: 'hidden' } : style}
      {...(value !== undefined ? { value } : {})}
      {...props}
    />
  )
})
